// Middleware API client.
//
// Auth: bearer token from window.CC_WIDGET_CONFIG (server-injected in production).
// Endpoint shapes match ~/clubchampion/middleware/src/api/ exactly.
//
// Availability is the trickiest piece: the middleware's /availability route
// is per-fitter (resourceId + ISO date range + durationMin). For the v2 widget's
// "Any Fitter" UX and cross-location fan-out (ADR 0003), the client fans out
// in parallel across fitters and merges the results. The middleware stays
// simple; the client orchestrates.

import type {
  Location,
  Resource,
  BookingPayload,
  BookingResponse,
  ValidatePromoResponse,
  WaitlistPayload,
  PostBookingPayload,
} from '../types';

const config = (): { apiBaseUrl: string; bearerToken: string } => {
  const c = window.CC_WIDGET_CONFIG ?? {};
  if (!c.apiBaseUrl) {
    throw new Error(
      '[cc-widget] window.CC_WIDGET_CONFIG.apiBaseUrl is not set. ' +
        'The host page must inject runtime config before the widget loads.',
    );
  }
  if (!c.bearerToken) {
    throw new Error(
      '[cc-widget] window.CC_WIDGET_CONFIG.bearerToken is not set. ' +
        'The host page must mint a per-session token server-side and inject it.',
    );
  }
  return { apiBaseUrl: c.apiBaseUrl.replace(/\/$/, ''), bearerToken: c.bearerToken };
};

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiBaseUrl, bearerToken } = config();
  const url = `${apiBaseUrl}${path}`;
  const method = (init?.method ?? 'GET').toUpperCase();
  const requestBody =
    typeof init?.body === 'string' ? init.body : init?.body ? String(init.body) : undefined;
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${bearerToken}`);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const startedAt = performance.now();
  const traceId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  let response: Response;
  try {
    response = await fetch(url, { ...init, headers });
  } catch (err) {
    emitTrafficEvent({
      id: traceId,
      method,
      path,
      status: 0,
      durationMs: Math.round(performance.now() - startedAt),
      requestBody,
      responseBody: err instanceof Error ? err.message : String(err),
      ok: false,
    });
    throw err;
  }

  const durationMs = Math.round(performance.now() - startedAt);
  const text = await response.text().catch(() => '');

  emitTrafficEvent({
    id: traceId,
    method,
    path,
    status: response.status,
    durationMs,
    requestBody,
    responseBody: text,
    ok: response.ok,
  });

  if (!response.ok) {
    throw new ApiError(response.status, `${response.status} ${response.statusText}`, text);
  }

  // Empty 204 responses have no body — return undefined to satisfy the caller's
  // Promise<T>. Callers that expect a value won't await void endpoints.
  if (!text) return undefined as unknown as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}

// ─── Traffic instrumentation (for the in-widget TRAFFIC drawer) ────────────

export interface TrafficEntry {
  id: string;
  timestamp: number;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  requestBody?: string;
  responseBody?: string;
  ok: boolean;
}

function emitTrafficEvent(entry: Omit<TrafficEntry, 'timestamp'>): void {
  const full: TrafficEntry = { ...entry, timestamp: Date.now() };
  window.dispatchEvent(new CustomEvent('cc-api-traffic', { detail: full }));
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Reads ──────────────────────────────────────────────────────────────────

export async function listLocations(): Promise<Location[]> {
  const { locations } = await apiFetch<{ locations: Location[] }>('/locations');
  return locations;
}

export async function listResources(): Promise<Resource[]> {
  const { resources } = await apiFetch<{ resources: Resource[] }>('/resources');
  return resources;
}

// ─── Availability (per-fitter + client-side fan-out for "Any Fitter") ──────

export interface MergedSlot {
  start: string;
  end: string;
  fitterId: string;
  fitterName: string;
}

interface MiddlewareAvailabilityResponse {
  resourceId: string;
  range: { start: string; end: string };
  mode: string;
  slots: Array<{ start: string; end: string }>;
}

async function fetchOneFitterAvailability(
  fitter: Resource,
  date: string,
  durationMin: number,
  fittingType?: string,
): Promise<MergedSlot[]> {
  const params = new URLSearchParams({
    resourceId: fitter.id,
    start: date,
    end: date,
    durationMin: String(durationMin),
  });
  if (fittingType) params.set('fittingType', fittingType);
  const res = await apiFetch<MiddlewareAvailabilityResponse>(`/availability?${params}`);
  return res.slots.map((s) => ({
    start: s.start,
    end: s.end,
    fitterId: fitter.id,
    fitterName: fitter.name,
  }));
}

export interface AvailabilityRequest {
  // Either resource (single fitter) OR location (fan out across all fitters there)
  resource?: Resource;
  location?: Location;
  // The cached resource list for fanout — saves a fetch if the caller has it
  knownResources?: Resource[];
  date: string;
  durationMin: number;
  /** Selected fitting type name, forwarded so SF returns slots scoped to it. */
  fittingType?: string;
}

export interface AvailabilityResponse {
  slots: MergedSlot[];
}

/**
 * Get availability for a fitter (single call) or a whole location ("Any Fitter",
 * fans out per-fitter in parallel, merges, sorts by start time, de-dupes).
 */
/**
 * Get unique YYYY-MM-DD dates that have at least one available slot across
 * all fitters at a location, for a given fitting type, over a date range.
 * Used by the Calendar to grey out days with no availability.
 *
 * Fans out across fitters in parallel (Promise.allSettled — a single fitter's
 * failure doesn't kill the batch). Each fitter's response covers the entire
 * range, so this is roughly one middleware call per fitter — typically 5
 * calls total for a multi-month horizon.
 */
export async function getAvailableDatesInRange(req: {
  location: Location;
  startDate: string;
  endDate: string;
  durationMin: number;
  fittingType?: string;
  knownResources?: Resource[];
  /**
   * Filter to a single fitter's availability. When unset, the date set is
   * the union of every fitter at the location ("any fitter" intent). When
   * set, only that fitter's date set is returned — used after the user
   * picks a specific fitter so the calendar shows only days they're working.
   */
  resourceId?: string;
}): Promise<Set<string>> {
  const fitters =
    req.knownResources ??
    (await listResources()).filter((r) => r.locationId === req.location.id);
  const atLoc = req.resourceId
    ? fitters.filter((r) => r.id === req.resourceId)
    : fitters.filter((r) => r.locationId === req.location.id);
  if (atLoc.length === 0) return new Set();

  const results = await Promise.allSettled(
    atLoc.map(async (f) => {
      const params = new URLSearchParams({
        resourceId: f.id,
        start: req.startDate,
        end: req.endDate,
        durationMin: String(req.durationMin),
      });
      if (req.fittingType) params.set('fittingType', req.fittingType);
      const res = await apiFetch<MiddlewareAvailabilityResponse>(`/availability?${params}`);
      return res.slots;
    }),
  );

  const dates = new Set<string>();
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    for (const slot of r.value) {
      // slot.start is an ISO date-time; extract YYYY-MM-DD in local time
      // (matches the Calendar's local-time YYYY-MM-DD generation)
      const d = new Date(slot.start);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      dates.add(`${y}-${m}-${day}`);
    }
  }
  return dates;
}

export async function getAvailability(req: AvailabilityRequest): Promise<AvailabilityResponse> {
  if (req.resource) {
    const slots = await fetchOneFitterAvailability(req.resource, req.date, req.durationMin, req.fittingType);
    return { slots: slots.sort((a, b) => a.start.localeCompare(b.start)) };
  }

  if (req.location) {
    const fitters =
      req.knownResources ??
      (await listResources()).filter((r) => r.locationId === req.location!.id);

    const atLoc = fitters.filter((r) => r.locationId === req.location!.id);

    if (atLoc.length === 0) return { slots: [] };

    // Parallel fan-out per ADR 0003; settle so one failure doesn't kill the batch
    const results = await Promise.allSettled(
      atLoc.map((f) => fetchOneFitterAvailability(f, req.date, req.durationMin, req.fittingType)),
    );
    const allSlots = results
      .filter((r): r is PromiseFulfilledResult<MergedSlot[]> => r.status === 'fulfilled')
      .flatMap((r) => r.value);

    // De-dupe by start time — when the same slot is open with multiple fitters,
    // show it once (pick the first fitter; the booking flow will respect their availability)
    const seen = new Set<string>();
    const deduped = allSlots.filter((s) => {
      if (seen.has(s.start)) return false;
      seen.add(s.start);
      return true;
    });

    return { slots: deduped.sort((a, b) => a.start.localeCompare(b.start)) };
  }

  throw new Error('getAvailability requires either resource or location');
}

// ─── Writes ─────────────────────────────────────────────────────────────────
// Schedule + Cancel cleared for sandbox testing 2026-05-24 (per Druv).
// See ~/.claude/projects/.../memory/project_schedule_cancel_unblocked.md

export async function createBooking(payload: BookingPayload): Promise<BookingResponse> {
  return apiFetch<BookingResponse>('/bookings', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Mirrors createBooking. The middleware endpoint is pending (Druv); on
// success returns a BookingResponse-shaped record with `id` prefixed
// `WL-` and `status: 'waitlisted'`. Called from TimeSlots' no-availability
// state so users have an actionable next step instead of a dead-end.
export async function createWaitlist(payload: WaitlistPayload): Promise<BookingResponse> {
  return apiFetch<BookingResponse>('/waitlist', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ─── Promo validation ──────────────────────────────────────────────────────
//
// Mirrors the booking call shape but returns validation + discount info
// only. The middleware endpoint is pending (Druv); calling this in advance
// of the endpoint landing will surface as a 404 from the middleware and
// the validate button will show a generic "couldn't validate" message.

export interface ValidatePromoRequest {
  promoCode: string;
  /** Store ID — required by SF /PromoValidate to scope the code to a store. */
  locationId: string;
  /** Fitting name — required to evaluate fitting-specific eligibility. */
  fittingTypeName: string;
  /** ISO slot start time — promo windows are checked against this. */
  appointmentStartDateTime: string;
  /** Cart subtotal before discount, in store currency. */
  price: number;
  /** Optional pre-pay subtotal when the store has prepayment configured. */
  prePayPrice?: number;
  /** Optional — enables Account-scoped, referral, or secondary codes. */
  email?: string;
}

export async function validatePromo(
  req: ValidatePromoRequest,
): Promise<ValidatePromoResponse> {
  return apiFetch<ValidatePromoResponse>('/promos/validate', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// Persists the post-booking GetSetUp form: ball fitting interest, phone,
// SMS opt-in, newsletter opt-in. Pending Druv. Failures don't block the
// user from completing the flow; the form surfaces the error inline and
// retains the entered data so the user can retry.
export async function submitPostBookingForm(payload: PostBookingPayload): Promise<void> {
  await apiFetch<unknown>('/post-booking', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function cancelBooking(
  bookingId: string,
  customerHint?: { email?: string; phone?: string },
): Promise<void> {
  const params = new URLSearchParams();
  if (customerHint?.email) params.set('email', customerHint.email);
  if (customerHint?.phone) params.set('phone', customerHint.phone);
  const qs = params.toString();
  await apiFetch<void>(`/bookings/${encodeURIComponent(bookingId)}${qs ? `?${qs}` : ''}`, {
    method: 'DELETE',
  });
}
